import { Component, inject, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { UploadZoneComponent } from './upload-zone/upload-zone.component';
import { Document } from '../../models/document.model';
import { DatePipe } from '@angular/common';
import { UploadDialogDetailComponent } from './upload-dialog-detail/upload-dialog-detail.component';
import { IngestionApiService } from '../../services/ingestion-api.service';
import { catchError, from, of, Subject, switchMap, timer } from 'rxjs';
import { AntiMemLeak } from '../../shared/classes/anti-mem-leak';
import { CommonModule } from '@angular/common';
import { TagModule } from 'primeng/tag';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Tooltip } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { SessionsService } from '../../modules/besharp-sessions/lib/services/sessions.service';

@Component({
  selector: 'app-upload-page',
  templateUrl: './upload-page.component.html',
  styleUrls: ['./upload-page.component.scss'],
  imports: [
    ButtonModule,
    UploadZoneComponent,
    DatePipe,
    UploadDialogDetailComponent,
    CommonModule,
    TagModule,
    ReactiveFormsModule,
    Tooltip,
    ToastModule,
    ConfirmDialog,
  ],
})
export class UploadPageComponent extends AntiMemLeak implements OnInit {
  private startPolling$ = new Subject<void>();

  showDialog: boolean = false;
  documentSelected: Document | null = null;
  refreshState: boolean = false;
  POLLING_INTERVAL = 10000;
  allDocuments: Document[] = [];
  documentsFiltred: Document[] = [];
  filterSerchBar: FormControl = new FormControl('');

  StatusColorMap: Record<string, string> = {
    UPLOADING: 'var(--blue-500)',
    UPLOADED: 'var(--slate-500)',
    PROCESSING: 'var(--amber-500)',
    PROCESSED: 'var(--emerald-500)',
    READY: 'var(--green-500)',
    UPDATING: 'var(--sky-500)',
    ERROR: 'var(--red-500)',
    CANCELLING: 'var(--orange-500)',
  };

  constructor() {
    super();
  }

  private ingestionService = inject(IngestionApiService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private sessionsService = inject(SessionsService);

  async ngOnInit() {
    this.filterSerchBar.valueChanges.subscribe((searchValue: string | null) => {
      if (!searchValue) {
        this.documentsFiltred = [...this.allDocuments];
        return;
      }

      const searchLower = searchValue.toLowerCase();

      this.documentsFiltred = this.allDocuments.filter((item: Document) => {
        const name = item.document_id?.toLowerCase().includes(searchLower);
        const categoryMatch = item.metadata?.category?.toLowerCase().includes(searchLower);
        const descMatch = item.metadata?.description?.toLowerCase().includes(searchLower);

        return name || categoryMatch || descMatch;
      });
    });

    this.subscriptions.add(
      this.startPolling$
        .pipe(
          switchMap(() => timer(0, this.POLLING_INTERVAL)),

          switchMap(() =>
            from(this.ingestionService.getDocuments()).pipe(
              catchError(() => {
                return of(null);
              })
            )
          )
        )
        .subscribe((resp) => {
          if (resp) {
            this.allDocuments = resp || [];

            const currentSearch = this.filterSerchBar.value?.toLowerCase() || '';

            if (!currentSearch) {
              this.documentsFiltred = [...this.allDocuments];
            } else {
              this.documentsFiltred = this.allDocuments.filter((item: Document) => {
                const categoryMatch = item.metadata?.category?.toLowerCase().includes(currentSearch);
                const descMatch = item.metadata?.description?.toLowerCase().includes(currentSearch);
                return categoryMatch || descMatch;
              });
            }

            this.refreshState = true;
            setTimeout(() => {
              this.refreshState = false;
            }, 2000);
          }
        })
    );

    this.triggerPolling();
  }
  triggerPolling() {
    this.startPolling$.next();
  }
  async loadDocument(): Promise<void> {
    this.refreshState = true;

    try {
      const resp = await this.ingestionService.getDocuments();
      this.allDocuments = resp || [];

      this.documentsFiltred = [...this.allDocuments];
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        this.refreshState = false;
      }, 500);
    }
  }
  async onDownload(document_id: string) {
    try {
      const resp = await this.ingestionService.getDocumentPresignedUrl(document_id);
      if (resp.presigned_url) {
        await this.ingestionService.downloadDocument(resp.presigned_url);
      }
    } catch (e) {
      console.error(e);
    }
  }
  openDialog(document: Document) {
    this.documentSelected = document;
    this.showDialog = true;
  }
  onDelete(document_id: string) {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this document?',
      header: 'Confirmation',
      closable: true,
      closeOnEscape: false,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Save',
      },
      accept: () => {
        console.log('Deleted document:', document_id);
        try {
          const resp = this.ingestionService.deleteDocument(document_id);
          console.log('Response delete', resp);
          this.messageService.add({
            severity: 'success',
            summary: 'Confirmed',
            detail: `You have deleted document: ${document_id}`,
            life: 3000,
          });
        } catch (e) {
          console.error(e);
        }
      },
      reject: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Rejected',
          detail: 'You have rejected',
          life: 3000,
        });
      },
    });
  }
  async downloadDocuments() {
    const listDocuments: Document[] = await this.ingestionService.getDocuments();

    const contentText = listDocuments
      .map((doc) => {
        const dateStr = doc.updated_at ? new Date(doc.updated_at).toLocaleDateString() : 'N/A';

        return `- Name:    ${doc.document_id}\n  Updated: ${dateStr}`;
      })
      .join('\n\n');

    const header = `=========================================\n              DOCUMENT LIST\n=========================================\n\n`;
    const footer = `\n\n=========================================\nTotal documents: ${listDocuments.length}`;

    const text = `${header}${contentText || 'No documents found.'}${footer}`;

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `document_list_${new Date().toISOString().split('T')[0]}.txt`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  }
}
